<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonSectionNavigator                                |
| Section: components/Person                                       |
| Purpose: Compact floating navigation for person page sections.   |
+------------------------------------------------------------------+
-->
<template>
    <nav class="person-section-navigator" :aria-label="ariaLabel">
        <ul class="person-section-navigator__list">
            <li v-for="section in sections" :key="section.id">
                <button
                    type="button"
                    class="person-section-navigator__button"
                    :class="{ 'person-section-navigator__button--active': section.id === activeSectionId }"
                    :aria-labelledby="tooltipId(section.id)"
                    :aria-controls="section.id"
                    :aria-current="section.id === activeSectionId ? 'location' : undefined"
                    @click="scrollToSection(section.id)"
                >
                    <span class="person-section-navigator__marker" aria-hidden="true"></span>
                    <span
                        :id="tooltipId(section.id)"
                        class="person-section-navigator__tooltip"
                        role="tooltip"
                    >
                        {{ section.label }}
                    </span>
                </button>
            </li>
        </ul>
    </nav>
</template>

<script>
export default {
    name: 'PersonSectionNavigator',
    props: {
        sections: {
            type: Array,
            required: true
        },
        ariaLabel: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            activeSectionId: '',
            observer: null,
            animationFrame: null
        };
    },
    watch: {
        sections: {
            deep: true,
            handler() {
                this.$nextTick(this.refreshObserver);
            }
        }
    },
    mounted() {
        window.addEventListener('scroll', this.scheduleActiveSectionUpdate, { passive: true });
        window.addEventListener('resize', this.scheduleActiveSectionUpdate);
        this.refreshObserver();
    },
    beforeUnmount() {
        this.disconnectObserver();
        window.removeEventListener('scroll', this.scheduleActiveSectionUpdate);
        window.removeEventListener('resize', this.scheduleActiveSectionUpdate);

        if (this.animationFrame !== null) {
            window.cancelAnimationFrame(this.animationFrame);
        }
    },
    methods: {
        tooltipId(sectionId) {
            return `person-section-tooltip-${sectionId}`;
        },
        getSectionElements() {
            return this.sections
                .map(section => document.getElementById(section.id))
                .filter(Boolean);
        },
        refreshObserver() {
            this.disconnectObserver();

            const sectionElements = this.getSectionElements();
            if (sectionElements.length === 0) {
                this.activeSectionId = '';
                return;
            }

            this.observer = new IntersectionObserver(this.scheduleActiveSectionUpdate, {
                root: null,
                rootMargin: '-35% 0px -55% 0px',
                threshold: 0
            });

            sectionElements.forEach(element => this.observer.observe(element));
            this.updateActiveSection();
        },
        disconnectObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },
        scheduleActiveSectionUpdate() {
            if (this.animationFrame !== null) return;

            this.animationFrame = window.requestAnimationFrame(() => {
                this.animationFrame = null;
                this.updateActiveSection();
            });
        },
        updateActiveSection() {
            const sectionElements = this.getSectionElements();
            if (sectionElements.length === 0) {
                this.activeSectionId = '';
                return;
            }

            const viewportAnchor = window.innerHeight * 0.4;
            let activeElement = sectionElements[0];

            sectionElements.forEach(element => {
                if (element.getBoundingClientRect().top <= viewportAnchor) {
                    activeElement = element;
                }
            });

            const reachedPageEnd = window.innerHeight + window.scrollY
                >= document.documentElement.scrollHeight - 2;
            if (reachedPageEnd) {
                activeElement = sectionElements[sectionElements.length - 1];
            }

            this.activeSectionId = activeElement.id;
        },
        scrollToSection(sectionId) {
            const section = document.getElementById(sectionId);
            if (!section) return;

            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY - 24);

            this.activeSectionId = sectionId;
            window.scrollTo({
                top,
                behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
        }
    }
};
</script>

<style scoped>
.person-section-navigator {
    position: fixed;
    top: 50%;
    right: clamp(16px, 2vw, 32px);
    z-index: 850;
    padding: 6px 4px;
    border: 1px solid rgba(29, 29, 29, 0.08);
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.78);
    box-shadow: var(--shadow-card-sm);
    backdrop-filter: blur(8px);
    transform: translateY(-50%);
}

.person-section-navigator__list {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
}

.person-section-navigator__button {
    position: relative;
    display: flex;
    width: 40px;
    height: 16px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    align-items: center;
    justify-content: flex-end;
    cursor: pointer;
}

.person-section-navigator__marker {
    width: 12px;
    height: 2px;
    border-radius: var(--border-radius-pill);
    background: rgba(29, 29, 29, 0.4);
    transition: width .18s ease, height .18s ease, background-color .18s ease, box-shadow .18s ease;
}

.person-section-navigator__button:hover .person-section-navigator__marker {
    width: 18px;
    background: var(--brand-text-dark);
}

.person-section-navigator__button--active .person-section-navigator__marker {
    width: 22px;
    height: 3px;
    background: var(--brand-color-1);
}

.person-section-navigator__button:focus {
    outline: none;
}

.person-section-navigator__button:focus-visible .person-section-navigator__marker {
    box-shadow: 0 0 0 3px rgba(168, 30, 39, 0.2);
}

.person-section-navigator__tooltip {
    position: absolute;
    top: 50%;
    right: calc(100% + 8px);
    width: max-content;
    max-width: min(220px, 70vw);
    padding: 6px 9px;
    border-radius: var(--border-radius-sm);
    background: var(--brand-text-dark);
    color: var(--brand-text-light);
    box-shadow: var(--shadow-card-sm);
    font-size: var(--font-size-xs);
    font-weight: 600;
    line-height: 1.25;
    opacity: 0;
    pointer-events: none;
    transform: translate(4px, -50%);
    transition: opacity .16s ease, transform .16s ease;
    white-space: nowrap;
}

.person-section-navigator__tooltip::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 100%;
    border: 5px solid transparent;
    border-left-color: var(--brand-text-dark);
    transform: translateY(-50%);
}

.person-section-navigator__button:hover .person-section-navigator__tooltip,
.person-section-navigator__button:focus-visible .person-section-navigator__tooltip {
    opacity: 1;
    transform: translate(0, -50%);
}

@media (max-width: 860px) {
    .person-section-navigator {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .person-section-navigator__marker,
    .person-section-navigator__tooltip {
        transition: none;
    }
}
</style>
